import { ref, computed, watch, reactive, resolveComponent, openBlock, createElementBlock, normalizeClass, createCommentVNode, Fragment, renderList, normalizeStyle, createElementVNode, createVNode, toDisplayString, withModifiers, mergeProps } from 'vue';
import { createComponent } from './utils/create-component';
import propTypes from './utils/prop-types';
import { INPUT_EVENT, DELETE_EVENT } from './utils/constants';
import { isPromise } from './utils/validate';
import { toArray, addPxUnit } from './utils/format';
import Icon from './icon';
import ImagePreview from './image-preview';
import { callInterceptor } from './utils/interceptor';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var _createComponent$1 = createComponent('uploader'),
  className$1 = _createComponent$1.className;
// 上传卡片尺寸
var UPLOAD_CARD_SIZE = 88;
// 图片尺寸超出限制事件
var OVERSIZE_EVENT = 'oversize';
// 关闭图片全屏预览事件
var CLOSE_PREVIEW_EVENT = 'close-preview';
// 打开图片全屏预览事件
var OPEN_PREVIEW_EVENT = 'open-preview';
/**
 * @description: 图片状态
 */
var UPLOAD_STATUS = {
  uploading: 'uploading',
  failed: 'failed',
  error: 'error',
  done: 'done'
};
/**
 * @description: 图片预览区域卡片状态配置
 */
var PREVIEW_CARD_CONFIG = {
  iconName: {
    uploading: 'base_upload_24',
    failed: 'base_remind_24',
    error: 'base_remind_24'
  },
  iconExtraClass: {
    uploading: "".concat(className$1, "__preview_icon-rotate"),
    failed: '',
    error: ''
  },
  messageText: {
    uploading: '上传中',
    failed: '上传失败点击重试',
    error: '加载失败'
  }
};

var props = {
  /**
   * 图片列表 FileList
   */
  modelValue: propTypes.array().def([]),
  /**
   * 上传图标
   */
  uploadIcon: propTypes.string().def('base_add_24'),
  /**
   * 上传文案
   */
  uploadText: propTypes.string().def('上传图片'),
  /**
   * 自定义上传卡片样式
   */
  uploadStyleOptions: propTypes.object(),
  /**
   * 是否开启图片多选，部分安卓机型不支持
   */
  multiple: propTypes["boolean"]().def(false),
  /**
   * 图片选取模式，可选值为 camera (直接调起摄像头)
   */
  capture: propTypes.oneOfType([propTypes.string(), propTypes["null"]()]),
  /**
   * 是否禁用
   */
  disabled: propTypes["boolean"]().def(false),
  /**
   * 是否只读
   */
  readonly: propTypes["boolean"]().def(false),
  /**
   * 上传图片数量限制
   */
  maxCount: propTypes.oneOfType([propTypes.string(), propTypes.number()]).def(Infinity),
  /**
   * 文件大小限制，单位为byte
   */
  maxSize: propTypes.oneOfType([propTypes.string(), propTypes.number()]).def(Infinity),
  /**
   * 图片读取前的函数
   */
  beforeRead: propTypes["function"](),
  /**
   * 图片读取完成后的函数
   */
  afterRead: propTypes["function"](),
  /**
   * 是否在点击预览图后展示全屏图片预览
   */
  previewFullImage: propTypes["boolean"]().def(true),
  /**
   * 缩略图模式
   */
  imageFit: propTypes.string().def('fill'),
  /**
   * 支持选择的文件类型 input原生属性，默认为*，支持传null
   */
  accept: propTypes.string().def('*'),
  /**
   * 全屏图片预览的配置项
   */
  previewOptions: propTypes.object(),
  /**
   * 图片删除前的函数
   */
  beforeDelete: propTypes["function"]()
};
var emits = [INPUT_EVENT, OVERSIZE_EVENT, DELETE_EVENT, CLOSE_PREVIEW_EVENT, OPEN_PREVIEW_EVENT];

/**
 * @description: 获取图片的64编码
 * @param {File} file
 * @return {*}
 */
function asyncFileToDataURL(file) {
  return new Promise(function (resolve) {
    var reader = new FileReader();
    reader.onload = function (event) {
      resolve(event.target.result);
    };
    reader.readAsDataURL(file);
  });
}
/**
 * @description: 判断文件大小是否超出限制
 * @param {*}
 * @return {*}
 */
function isOversize(items, maxSize) {
  return toArray(items).some(function (item) {
    return item.file.size > +maxSize;
  });
}
/**
 * @description: 过滤文件
 * @param {*}
 * @return {*}
 */
function filterFiles(items, maxSize) {
  var valid = [];
  var invalid = [];
  items.forEach(function (item) {
    if (isOversize(item, maxSize)) {
      invalid.push(item);
    } else {
      valid.push(item);
    }
  });
  return {
    valid: valid,
    invalid: invalid
  };
}

var _components;
var _createComponent = createComponent('uploader'),
  create = _createComponent.create,
  className = _createComponent.className;
var script = create({
  components: (_components = {}, _defineProperty(_components, Icon.name, Icon), _defineProperty(_components, ImagePreview.name, ImagePreview), _components),
  props: props,
  emits: emits,
  setup: function setup(props, _ref) {
    var emit = _ref.emit;
    // 上传区域dom
    var inputRef = ref();
    var uploadFailedInputRef = ref();
    // 是否展示预览图片
    var previewFullImageShow = ref(false);
    // 预览图片的索引值
    var previewInitialIndex = ref(0);
    // 预览图片列表
    var previewImages = ref([]);
    // 是否展示上传按钮
    var showUpload = computed(function () {
      return props.modelValue && props.modelValue.length < +props.maxCount;
    });
    // 上传卡片自定义样式
    var uploadCardStyle = computed(function () {
      if (!props.uploadStyleOptions) {
        return {};
      } else {
        var uploadStyleOptions = props.uploadStyleOptions;
        var _uploadStyleOptions$w = uploadStyleOptions.width,
          width = _uploadStyleOptions$w === void 0 ? UPLOAD_CARD_SIZE : _uploadStyleOptions$w,
          _uploadStyleOptions$h = uploadStyleOptions.height,
          height = _uploadStyleOptions$h === void 0 ? UPLOAD_CARD_SIZE : _uploadStyleOptions$h,
          backgroundImage = uploadStyleOptions.backgroundImage;
        var style = {
          width: addPxUnit(width),
          height: addPxUnit(height)
        };
        backgroundImage && (style.backgroundImage = "url(".concat(backgroundImage, ")"));
        return style;
      }
    });
    // 上传卡片自定义图标颜色
    var uploadIconColor = computed(function () {
      var uploadStyleOptions = props.uploadStyleOptions;
      return uploadStyleOptions ? uploadStyleOptions.iconColor : '';
    });
    // 上传卡片自定义文字颜色
    var uploadTextColor = computed(function () {
      var uploadStyleOptions = props.uploadStyleOptions;
      return uploadStyleOptions ? uploadStyleOptions.textColor : '';
    });
    /**
     * @description: 触发图片选择事件
     * @param {Event, UploaderOnChangeExtraParams}
     * @return {*}
     */
    function onChange(event, extraParam) {
      var files = event.target.files;
      if (!files || !files.length) {
        return;
      }
      var fileList = Array.from(files);
      // 图片读取之前的操作
      if (props.beforeRead) {
        var response = props.beforeRead(props.multiple ? toArray(fileList) : fileList[0]);
        // 返回 true 或 false 的场景(true: 继续执行上传操作；false: 终止后续上传操作)
        if (!response) {
          resetInput();
          return;
        }
        // 返回 Promise 的场景
        if (isPromise(response)) {
          // resolve 代表继续执行上传操作（可将处理后的文件返回作为后续上传操作的文件）
          response.then(function (result) {
            if (result) {
              readFile(toArray(result), extraParam);
            } else {
              resetInput();
            }
          })["catch"](resetInput);
          resetInput();
          return;
        }
      }
      readFile(fileList, extraParam);
    }
    /**
     * @description: 读取文件
     * @param {File[], UploaderOnChangeExtraParams}
     * @return {*}
     */
    function readFile(files, extraParam) {
      var modelValue = props.modelValue,
        maxCount = props.maxCount;
      var len = modelValue.length || 0; // 当前的图片数量
      var remainCount = +maxCount - len || 1; // 用户还能上传的图片数量（兼容用户上传失败点击重试的场景）
      // 若用户上传的图片数量大于能上传的图片数量，则进行截取
      if (files.length > remainCount) {
        files = files.slice(0, remainCount);
      }
      // 得到一组获取 base64 信息的 promise 对象
      var promises = files.map(function (file) {
        return asyncFileToDataURL(file);
      });
      Promise.all(promises).then(function (base64Contents) {
        var fileList = files.map(function (file, index) {
          // 构造 afterRead 函数的参数(fileItem) 信息
          var result = {
            file: file,
            status: '',
            message: ''
          };
          // 将 base64 信息封装 fileItem 中
          if (base64Contents[index]) {
            result.base64Content = base64Contents[index];
          }
          return result;
        });
        // 执行文件读取后的方法
        onAfterRead(fileList, extraParam);
      });
    }
    /**
     * @description: 读取文件后
     * @param {UploaderFileItem[], UploaderOnChangeExtraParams}
     * @return {*}
     */
    function onAfterRead(fileList, extraParam) {
      resetInput();
      // 判断文件是否超出限制
      if (isOversize(fileList, props.maxSize)) {
        var _filterFiles = filterFiles(fileList, props.maxSize),
          valid = _filterFiles.valid,
          invalid = _filterFiles.invalid;
        // 过滤出有效的文件继续上传
        fileList = valid;
        // 无效的文件抛出事件并返回给用户
        emit(OVERSIZE_EVENT, props.multiple ? toArray(invalid) : invalid[0]);
        return;
      }
      var isUploadFailed = extraParam.isUploadFailed,
        index = extraParam.index;
      fileList = reactive(fileList);
      // 如果是重新上传的场景则在指定位置加入fileList
      if (isUploadFailed) {
        var files = props.modelValue.slice(0);
        files.splice.apply(files, [index, 1].concat(_toConsumableArray(fileList)));
        emit(INPUT_EVENT, files);
      } else {
        emit(INPUT_EVENT, [].concat(_toConsumableArray(props.modelValue), _toConsumableArray(toArray(fileList))));
      }
      // 执行读取文件完成后的回调函数
      props.afterRead && props.afterRead(props.multiple ? toArray(fileList) : fileList[0]);
    }
    /**
     * @description: 重置上传区域
     * @param {*}
     * @return {*}
     */
    function resetInput() {
      var inputEle = inputRef.value;
      var uploadFailedInputEle = uploadFailedInputRef.value;
      inputEle && (inputEle.value = '');
      uploadFailedInputEle && (uploadFailedInputEle.value = '');
    }
    /**
     * @description: 图片加载失败显示加载失败状态
     * @param {number}
     * @return {*}
     */
    function onError(index) {
      var fileList = props.modelValue.slice(0);
      fileList[index].status = UPLOAD_STATUS.error;
      emit(INPUT_EVENT, fileList);
    }
    /**
     * @description: button click 图片删除
     * @param {UploaderFileItem, number}
     * @return {*}
     */
    function onDelete(item, index) {
      callInterceptor(props.beforeDelete, {
        args: [item, index],
        done: function done() {
          var fileList = props.modelValue.slice(0);
          fileList.splice(index, 1);
          emit(DELETE_EVENT, item, index);
          emit(INPUT_EVENT, fileList);
        }
      });
    }
    /**
     * @description: button click 图片预览
     * @param {number}
     * @return {*}
     */
    function onPreview(index) {
      if (props.previewFullImage) {
        previewFullImageShow.value = true;
        previewInitialIndex.value = index;
      }
    }
    /**
     * @description: button click 打开图片预览
     * @param {*}
     * @return {*}
     */
    function onOpenPreview() {
      emit(OPEN_PREVIEW_EVENT);
    }
    /**
     * @description: button click 关闭图片预览
     * @param {*}
     * @return {*}
     */
    function onClosePreview() {
      emit(CLOSE_PREVIEW_EVENT);
    }
    /**
     * @description: 初始化预览图片
     * @param {*}
     * @return {*}
     */
    function initPreviewImages(fileList) {
      var imgs = [];
      fileList.forEach(function (item) {
        item.url && imgs.push(item.url);
      });
      previewImages.value = imgs;
    }
    watch(function () {
      return props.modelValue;
    }, function (val) {
      initPreviewImages(val);
    }, {
      immediate: true,
      deep: true
    });
    return {
      className: className,
      UPLOAD_STATUS: UPLOAD_STATUS,
      PREVIEW_CARD_CONFIG: PREVIEW_CARD_CONFIG,
      previewFullImageShow: previewFullImageShow,
      previewInitialIndex: previewInitialIndex,
      previewImages: previewImages,
      inputRef: inputRef,
      uploadCardStyle: uploadCardStyle,
      uploadIconColor: uploadIconColor,
      uploadTextColor: uploadTextColor,
      showUpload: showUpload,
      onChange: onChange,
      onDelete: onDelete,
      onPreview: onPreview,
      onError: onError,
      onClosePreview: onClosePreview,
      onOpenPreview: onOpenPreview
    };
  }
});

var _hoisted_1 = ["src", "onClick", "onError"];
var _hoisted_2 = ["onClick"];
var _hoisted_3 = ["multiple", "capture", "disabled", "accept", "onChange"];
var _hoisted_4 = ["capture", "multiple", "disabled", "accept"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  var _component_sp_icon = resolveComponent("sp-icon");
  var _component_sp_image_preview = resolveComponent("sp-image-preview");
  return openBlock(), createElementBlock("div", {
    "class": normalizeClass(_ctx.className)
  }, [createCommentVNode(" 图片预览列表 "), (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.modelValue, function (item, index) {
    return openBlock(), createElementBlock("div", {
      "class": normalizeClass("".concat(_ctx.className, "__preview")),
      style: normalizeStyle(_ctx.uploadCardStyle),
      key: index
    }, [createCommentVNode(" 上传成功 展示图片 "), item.url && item.status !== _ctx.UPLOAD_STATUS.error ? (openBlock(), createElementBlock("img", {
      key: 0,
      "class": normalizeClass("".concat(_ctx.className, "__preview_img")),
      style: normalizeStyle("objectFit: ".concat(_ctx.imageFit)),
      src: item.url,
      onClick: function onClick($event) {
        return _ctx.onPreview(index);
      },
      onError: function onError($event) {
        return _ctx.onError(index);
      }
    }, null, 46 /* CLASS, STYLE, PROPS, HYDRATE_EVENTS */, _hoisted_1)) : (openBlock(), createElementBlock(Fragment, {
      key: 1
    }, [createCommentVNode(" 上传中/上传失败/图片加载失败 显示图标和文案 "), createElementVNode("div", {
      "class": normalizeClass("".concat(_ctx.className, "__preview_progress"))
    }, [createVNode(_component_sp_icon, {
      "class": normalizeClass(["".concat(_ctx.className, "__preview_icon"), "".concat(_ctx.PREVIEW_CARD_CONFIG.iconExtraClass[item.status])]),
      name: _ctx.PREVIEW_CARD_CONFIG.iconName[item.status],
      color: _ctx.uploadIconColor
    }, null, 8 /* PROPS */, ["class", "name", "color"]), createElementVNode("div", {
      "class": normalizeClass("".concat(_ctx.className, "__preview_text")),
      style: normalizeStyle({
        color: _ctx.uploadTextColor
      })
    }, toDisplayString(_ctx.PREVIEW_CARD_CONFIG.messageText[item.status]), 7 /* TEXT, CLASS, STYLE */)], 2 /* CLASS */)], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */)), createCommentVNode(" 右上角关闭按钮 "), !_ctx.readonly ? (openBlock(), createElementBlock("div", {
      key: 2,
      "class": normalizeClass("".concat(_ctx.className, "__preview_delete")),
      onClick: withModifiers(function ($event) {
        return _ctx.onDelete(item, index);
      }, ["stop"])
    }, [createVNode(_component_sp_icon, {
      "class": normalizeClass("".concat(_ctx.className, "__preview_delete_icon")),
      name: "base_close_24"
    }, null, 8 /* PROPS */, ["class"])], 10 /* CLASS, PROPS */, _hoisted_2)) : createCommentVNode("v-if", true), createCommentVNode(" 上传失败状态允许重新上传 "), item.status == _ctx.UPLOAD_STATUS.failed ? (openBlock(), createElementBlock("input", {
      key: 3,
      ref_for: true,
      ref: "uploadFailedInputRef",
      "class": normalizeClass("".concat(_ctx.className, "__preview_input")),
      type: "file",
      multiple: _ctx.multiple,
      capture: _ctx.capture,
      disabled: _ctx.disabled,
      accept: _ctx.accept,
      onChange: function onChange($event) {
        return _ctx.onChange($event, {
          isUploadFailed: true,
          index: index
        });
      }
    }, null, 42 /* CLASS, PROPS, HYDRATE_EVENTS */, _hoisted_3)) : createCommentVNode("v-if", true)], 6 /* CLASS, STYLE */);
  }), 128 /* KEYED_FRAGMENT */)), createCommentVNode(" 图片上传卡片 "), _ctx.showUpload && !_ctx.readonly ? (openBlock(), createElementBlock("div", {
    key: 0,
    "class": normalizeClass(["".concat(_ctx.className, "__upload"), _ctx.disabled && "".concat(_ctx.className, "__upload-disabled")]),
    style: normalizeStyle(_ctx.uploadCardStyle)
  }, [createVNode(_component_sp_icon, {
    "class": normalizeClass("".concat(_ctx.className, "__upload_icon")),
    name: _ctx.uploadIcon,
    color: _ctx.uploadIconColor
  }, null, 8 /* PROPS */, ["class", "name", "color"]), createElementVNode("div", {
    "class": normalizeClass("".concat(_ctx.className, "__upload_text")),
    style: normalizeStyle({
      color: _ctx.uploadTextColor
    })
  }, toDisplayString(_ctx.uploadText), 7 /* TEXT, CLASS, STYLE */), createElementVNode("input", {
    ref: "inputRef",
    "class": normalizeClass("".concat(_ctx.className, "__upload_input")),
    type: "file",
    capture: _ctx.capture,
    multiple: _ctx.multiple,
    disabled: _ctx.disabled,
    accept: _ctx.accept,
    onChange: _cache[0] || (_cache[0] = function ($event) {
      return _ctx.onChange($event, {
        isUploadFailed: false,
        index: _ctx.modelValue.length - 1
      });
    })
  }, null, 42 /* CLASS, PROPS, HYDRATE_EVENTS */, _hoisted_4)], 6 /* CLASS, STYLE */)) : createCommentVNode("v-if", true), createCommentVNode(" 图片全屏预览 "), createVNode(_component_sp_image_preview, mergeProps(_ctx.previewOptions, {
    modelValue: _ctx.previewFullImageShow,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = function ($event) {
      return _ctx.previewFullImageShow = $event;
    }),
    images: _ctx.previewImages,
    initIndex: _ctx.previewInitialIndex,
    onClose: _ctx.onClosePreview,
    onOpen: _ctx.onOpenPreview
  }), null, 16 /* FULL_PROPS */, ["modelValue", "images", "initIndex", "onClose", "onOpen"])], 2 /* CLASS */);
}

script.render = render;
script.__file = "components/src/component/uploader/uploader.vue";

/*
 * @Description: uploader 图片上传组件导出文件
 * @Author: duqiao@kuaishou.com
 * @Date: 2022-03-22 19:48:51
 * @LastEditors: zhoushichao@kuaishou.com
 * @LastEditTime: 2023-02-02 11:25:34
 */
var _Uploader = script;

export { _Uploader as default };
